import type { SnackbarProps } from '@components/Snackbar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type SnackbarActions = {
  clear: () => void;
  info: (message: string, autoHide?: boolean) => void;
  success: (message: string, autoHide?: boolean) => void;
  error: (message: string, error?: unknown, autoHide?: boolean) => void;
  warning: (message: string, error?: unknown, autoHide?: boolean) => void;
};

export type UseSnackbar = [SnackbarProps, SnackbarActions];

const TIMEOUT_MS = 7 * 1000;
const DEFAULT_STATE: SnackbarProps = { open: false, message: '' };

export const useSnackbar = (): UseSnackbar => {
  const [state, setState] = useState<SnackbarProps>(DEFAULT_STATE);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const clear = useCallback(() => {
    clearTimeout(timerRef.current);
    setState(DEFAULT_STATE);
  }, []);

  // prevents a stale setState firing after the owning component unmounts
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const showMessage = useCallback(
    (
      message: string,
      variant: 'error' | 'default' | 'success' = 'default',
      autoHide = true,
    ) => {
      clearTimeout(timerRef.current);
      if (autoHide) timerRef.current = setTimeout(clear, TIMEOUT_MS);
      setState((prev) => ({
        ...prev,
        message,
        variant,
        open: true,
        onClose: clear,
      }));
    },
    [clear],
  );

  const info = useCallback(
    (message: string, autoHide?: boolean) =>
      showMessage(message, 'default', autoHide),
    [showMessage],
  );

  const error = useCallback(
    (message: string, _?: unknown, autoHide?: boolean) =>
      showMessage(message, 'error', autoHide),
    [showMessage],
  );

  const success = useCallback(
    (message: string, autoHide?: boolean) =>
      showMessage(message, 'success', autoHide),
    [showMessage],
  );

  const warning = useCallback(
    (message: string, _?: unknown, autoHide?: boolean) =>
      showMessage(message, 'default', autoHide),
    [showMessage],
  );

  const actions = useMemo<SnackbarActions>(
    () => ({ clear, info, error, success, warning }),
    [clear, info, error, success, warning],
  );

  return useMemo<UseSnackbar>(() => [state, actions], [state, actions]);
};
